import React, { useState, useMemo, useCallback } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Button, Modal, ModalBody, Badge } from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useDeleteSAUser } from "../../../api/users/service/index";
import { useNavigate } from "react-router-dom";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { useGetSAUsers, useGetTenants } from "api/super-admin/service";
import UsersFilters from "../../users/components/UsersFilters";
import UsersCards from "../../users/components/UsersCards";

const User = ({ activeColor = "secondary", account, shellThemeVariant = "1" }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimer = React.useRef(null);
  const [tenant, setTenant] = useState("");

  const { data: tenantsData, isLoading: isLoadingTenants } = useGetTenants(1, 1000);

  React.useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(searchTimer.current);
  }, [searchInput]);

  const isSearching = debouncedSearch.length >= 2;

  const { data: usersResponse, pagination, isLoading } = useGetSAUsers(
    page,
    pageSize,
    tenant,
    debouncedSearch,
    true
  );

  const userResults = useMemo(() => {
    if (Array.isArray(usersResponse)) return usersResponse;
    return usersResponse?.results ?? [];
  }, [usersResponse]);

  const pageCount = pagination?.pageCount ?? usersResponse?.pagination?.pageCount ?? 1;
  const totalItems = pagination?.total ?? usersResponse?.pagination?.total;

  const deleteUserMutation = useDeleteSAUser();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteUser, setSelectedDeleteUser] = useState(null);

  const isTheme2 = shellThemeVariant === "2";
  const UsersTable = isTheme2 ? ReactTable2 : ReactTable;

  const tenantOptions = useMemo(
    () =>
      (tenantsData?.data || tenantsData?.results || []).map((row) => ({
        value: String(row.code),
        label: row.name,
      })) || [],
    [tenantsData]
  );

  const tenantValue = useMemo(
    () =>
      tenant
        ? {
            value: tenant,
            label:
              tenantOptions.find((opt) => String(opt.value) === String(tenant))?.label || tenant,
          }
        : null,
    [tenant, tenantOptions]
  );

  const usersData = useMemo(
    () =>
      userResults.map((user) => ({
        id: user.id || "-",
        documentId: user.id || "-",
        fullName: user.user_detail?.full_name || user.username || "-",
        email: user.email || "-",
        phone: user.user_detail?.work_phone || "-",
        tenants:
          user.tenants && user.tenants.length > 0
            ? user.tenants.length === 1
              ? user.tenants[0].name
              : `+${user.tenants.length}`
            : "-",
        tenantsBadge:
          user.tenants && user.tenants.length > 0 ? (
            <Badge
              color={user.tenants.length === 1 ? "info" : "success"}
              className="badge-pill"
              style={{ textTransform: "none" }}
            >
              {user.tenants.length === 1 ? user.tenants[0].name : `+${user.tenants.length}`}
            </Badge>
          ) : (
            "-"
          ),
        active: user.confirmed ? t("usersList.yes") : t("usersList.no"),
        activeBadge: (
          <Badge color={user.confirmed ? "success" : "danger"} className="badge-pill">
            {user.confirmed ? t("usersList.yes") : t("usersList.no")}
          </Badge>
        ),
      })),
    [userResults, t]
  );

  const handleDeleteClick = useCallback((user) => {
    setSelectedDeleteUser(user);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = () => {
    if (selectedDeleteUser) {
      deleteUserMutation.mutate(selectedDeleteUser.id, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setSelectedDeleteUser(null);
        },
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteUser(null);
  };

  const userColumns = useMemo(
    () => [
      { Header: t("usersList.columns.name"), accessor: "fullName" },
      { Header: t("usersList.columns.email"), accessor: "email" },
      { Header: t("usersList.columns.phone"), accessor: "phone" },
      {
        Header: t("usersList.columns.active"),
        accessor: isTheme2 ? "active" : "activeBadge",
        headerClassName: "text-center",
        className: "text-center",
      },
      {
        Header: t("searchInventory.tenant"),
        accessor: isTheme2 ? "tenants" : "tenantsBadge",
        headerClassName: "text-center",
        className: "text-center",
      },
      {
        id: "actions",
        Header: t("usersList.columns.actions"),
        accessor: "id",
        disableSortBy: true,
        headerClassName: "text-right",
        className: "text-right",
        Cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="actions-right">
              <Button
                color="link"
                size="sm"
                className="btn-icon btn-link like table-edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/super-admin/users/${r.id}`);
                }}
              >
                <i className="tim-icons icon-pencil" />
              </Button>
              <Button
                color="link"
                size="sm"
                className="btn-icon btn-link like table-delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(r);
                }}
                disabled={deleteUserMutation.isPending}
              >
                {deleteUserMutation.isPending && selectedDeleteUser?.id === r.id ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                ) : (
                  <i className="tim-icons icon-simple-remove" />
                )}
              </Button>
            </div>
          );
        },
      },
    ],
    [t, isTheme2, navigate, handleDeleteClick, deleteUserMutation.isPending, selectedDeleteUser?.id]
  );

  const usersNoDataText = isSearching
    ? "No users found for your search."
    : t("searchDeals.noRecordsFound");

  const headerActions =
    account?.permissions?.users_add ? (
      <Button
        size="sm"
        style={{ ...activeColorStyle, color: "white" }}
        onClick={() => navigate("/super-admin/users/add")}
      >
        {t("usersList.addUser")}
      </Button>
    ) : null;

  const filters = (
    <UsersFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      searchInput={searchInput}
      onSearchChange={setSearchInput}
      searchLabel={`${t("searchDeals.search")}:`}
      showTenantFilter
      onTenantChange={(value) => {
        setTenant(value);
        setPage(1);
      }}
      tenantValue={tenantValue}
      tenantOptions={tenantOptions}
      isLoadingTenants={isLoadingTenants}
      tenantLabel={t("searchInventory.tenant")}
    />
  );

  const tableProps = {
    activeColor,
    data: usersData,
    columns: userColumns,
    manualPagination: !isSearching,
    pageCount: isSearching ? 1 : pageCount,
    pageIndex: page - 1,
    totalItems: !isSearching ? totalItems : undefined,
    pageSize,
    noDataText: usersNoDataText,
    loading: isLoading,
    loadingText: t("searchDeals.loading"),
    onPageChange: (newPageIndex) => setPage(newPageIndex + 1),
    onPageSizeChange: (newSize) => {
      setPageSize(newSize);
      setPage(1);
    },
    onRowClick: (rowData) => {
      navigate(`/super-admin/users/${rowData.id}`);
    },
  };

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: isTheme2 ? "13px" : undefined, minWidth: 0, maxWidth: "100%" }}
    >
      {isTheme2 ? (
        <UsersTable {...tableProps} className="-striped -highlight sticky-stock-table" />
      ) : (
        <UsersTable
          {...tableProps}
          showPaginationTop
          showPaginationBottom={false}
          className="-striped -highlight"
        />
      )}
    </div>
  );

  return (
    <>
      <div
        className={classNames("content", "search-users", "search-users--sa", {
          "search-users--theme2": isTheme2,
          "theme2-admin-content-compact": isTheme2,
          "theme2-users-inset": isTheme2,
        })}
      >
        <UsersCards
          isTheme2={isTheme2}
          filters={filters}
          table={table}
          headerActions={headerActions}
          title={`Super Admin - ${t("usersList.title")}`}
        />
      </div>

      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t("usersList.modal.title")}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t("usersList.modal.confirm", { name: selectedDeleteUser?.fullName || "" })}</p>
          <p className="text-muted">{t("usersList.modal.cannotUndo")}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteUserMutation.isPending}
          >
            {t("usersList.modal.cancel")}
          </Button>
          <Button
            style={{
              background: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage: "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                {t("usersList.modal.deleting")}
              </>
            ) : (
              t("usersList.modal.yesDelete")
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default User;
